import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Phone, Send, CheckCircle, ArrowLeft, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Pre-fill message from URL param (e.g. from Enterprise contact button)
  useEffect(() => {
    const prefill = searchParams.get("message");
    if (prefill) setMessage(prefill);
  }, [searchParams]);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = name.trim() && (email.trim() || phone.trim()) && message.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name, email, phone, message });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send message. Please try again.");
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #F97316 0%, #D946EF 100%)" }}>
            <CheckCircle size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
            Message Sent
          </h1>
          <p className="text-slate-500 mb-8">
            Thank you, {name}. We'll get back to you within 24 hours.
          </p>
          <Link to="/">
            <Button variant="outline" className="rounded-xl">
              <ArrowLeft size={16} className="mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="border-b border-slate-100">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/adbit-logo.png" alt="AdBit" className="h-8 w-auto" />
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-orange-600 transition-colors flex items-center gap-1">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </nav>

      <div className="max-w-[1200px] mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Left — Info */}
          <div>
            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
              Get in Touch
            </h1>
            <p className="text-slate-500 text-lg mb-10 leading-relaxed">
              Have a question, need help, or want to share feedback? We'd love to hear from you.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <Mail size={18} className="text-orange-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Email Us</p>
                  <a href="mailto:support@adbit.digital" className="text-sm text-orange-600 hover:underline">
                    support@adbit.digital
                  </a>
                  <p className="text-xs text-slate-400 mt-1">We respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <MessageSquare size={18} className="text-orange-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Common Topics</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Account & billing questions, feature requests, partnership inquiries, bug reports, or general feedback.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div>
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
              <h2 className="text-lg font-bold mb-6" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
                Send us a message
              </h2>

              {error && (
                <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
                  {error}
                </div>
              )}

              {/* Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Name <span className="text-red-400">*</span>
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="rounded-xl h-11"
                  required
                />
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email <span className="text-xs text-slate-400">(email or phone required)</span>
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-xl h-11"
                />
              </div>

              {/* Phone */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Phone <span className="text-xs text-slate-400">(email or phone required)</span>
                </label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="rounded-xl h-11"
                />
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us how we can help..."
                  rows={5}
                  required
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/20 focus:border-orange-400 resize-none"
                />
              </div>

              {/* Validation hint */}
              {!email.trim() && !phone.trim() && (name.trim() || message.trim()) && (
                <p className="text-xs text-amber-600 mb-4">
                  Please provide at least an email or phone number so we can reach you.
                </p>
              )}

              <Button
                type="submit"
                disabled={!canSubmit || sending}
                className="w-full rounded-xl h-12 text-white border-0 text-base font-semibold disabled:opacity-50"
                style={{ background: canSubmit ? "linear-gradient(135deg, #F97316 0%, #D946EF 100%)" : "#E2E8F0" }}
              >
                {sending ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send size={16} /> Send Message
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8">
        <div className="max-w-[1200px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-400">&copy; 2026 AdBit. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/terms" className="text-xs text-slate-400 hover:text-orange-600">Terms</Link>
            <Link to="/privacy" className="text-xs text-slate-400 hover:text-orange-600">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
